/**
 * useSidebarPins — Foundation für User-konfigurierbare Sidebar-Pins.
 * Spec: docs/design-spec.md §2.1.
 *
 * Persistiert in `localStorage['paypilot.sidebar.pins']`.
 * Trigger-Mechanik (Rechtsklick-Menue auf NavLinks, Drag-to-Reorder) wird
 * später nachgereicht — diese Foundation liefert nur Read/Write/Render.
 */
import { useCallback, useEffect, useState } from "react";

export interface SidebarPin {
  /** Eindeutiger Key — typischerweise der Pfad des gepinnten Items. */
  id: string;
  label: string;
  href: string;
  /** Optionaler Lucide-Icon-Name für spätere Erweiterung. */
  icon?: string;
}

const STORAGE_KEY = "paypilot.sidebar.pins";
const MAX_PINS = 8;

function readInitial(): SidebarPin[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (p): p is SidebarPin =>
          typeof p === "object" &&
          p !== null &&
          typeof (p as SidebarPin).id === "string" &&
          typeof (p as SidebarPin).label === "string" &&
          typeof (p as SidebarPin).href === "string",
      )
      .slice(0, MAX_PINS);
  } catch {
    return [];
  }
}

function persist(pins: SidebarPin[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pins));
  } catch {
    // ignore (Privacy-Mode/Quota)
  }
}

export interface UseSidebarPinsResult {
  pins: SidebarPin[];
  addPin: (pin: SidebarPin) => void;
  removePin: (id: string) => void;
  reorderPins: (newOrder: SidebarPin[]) => void;
  isPinned: (id: string) => boolean;
}

export function useSidebarPins(): UseSidebarPinsResult {
  const [pins, setPins] = useState<SidebarPin[]>(readInitial);

  // Cross-tab Sync via storage-Event (defensive — harmless wenn nichts kommt).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setPins(readInitial());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addPin = useCallback((pin: SidebarPin) => {
    setPins((cur) => {
      if (cur.some((p) => p.id === pin.id)) return cur;
      const next = [...cur, pin].slice(0, MAX_PINS);
      persist(next);
      return next;
    });
  }, []);

  const removePin = useCallback((id: string) => {
    setPins((cur) => {
      const next = cur.filter((p) => p.id !== id);
      persist(next);
      return next;
    });
  }, []);

  const reorderPins = useCallback((newOrder: SidebarPin[]) => {
    setPins(() => {
      const next = newOrder.slice(0, MAX_PINS);
      persist(next);
      return next;
    });
  }, []);

  const isPinned = useCallback((id: string) => pins.some((p) => p.id === id), [pins]);

  return { pins, addPin, removePin, reorderPins, isPinned };
}
